import { getUserFriendlyErrorMessage, shouldReloadOnError } from '../../utils/errorHandling';

/**
 * Global error fallback component for application-level errors
 * Displays when the entire app crashes
 */
const GlobalErrorFallback = ({ error, resetErrorBoundary }) => {
  const userMessage = getUserFriendlyErrorMessage(error);
  const shouldReload = shouldReloadOnError(error);

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
          {/* Error Icon */}
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <svg 
                className="w-8 h-8 text-red-600 dark:text-red-400" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" 
                />
              </svg>
            </div>
          </div>

          {/* Error Message */}
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Oops! Something went wrong
          </h1>
          
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {userMessage}
          </p>

          {/* Development Error Details */}
          {process.env.NODE_ENV === 'development' && error && (
            <details className="mb-6 text-left">
              <summary className="cursor-pointer text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                🔧 Developer Details
              </summary>
              <div className="mt-3 p-3 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono overflow-auto max-h-32">
                <div className="text-red-600 dark:text-red-400 font-semibold">
                  {error.name}: {error.message}
                </div>
                {error.stack && (
                  <pre className="mt-2 text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                    {error.stack.split('\n').slice(0, 5).join('\n')}
                  </pre>
                )}
              </div>
            </details>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {shouldReload ? (
              <button
                onClick={handleReload}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                Reload Application
              </button>
            ) : (
              <button
                onClick={resetErrorBoundary}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                Try Again
              </button>
            )}
            
            <button
              onClick={handleGoHome}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
            >
              Go to Home
            </button>
          </div>

          {/* Support Information */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              If this problem persists, please contact support with error ID: {' '}
              <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">
                {Date.now().toString(36)}
              </code>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalErrorFallback;